import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Share2, Play, Pause } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { extractAndValidateYouTubeId } from '@/utils/videoHelpers';

interface GalleryItem {
  id: string;
  type: 'image' | 'video';
  src: string;
  alt?: string;
  thumbnail?: string;
  /** Opcional: código embed completo (ex: <iframe ...></iframe>) */
  embedHtml?: string;
}

interface GalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: GalleryItem[];
  initialIndex?: number;
  onShare?: (item: GalleryItem) => void;
}

const GalleryModal: React.FC<GalleryModalProps> = ({
  isOpen,
  onClose,
  items,
  initialIndex = 0,
  onShare
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const isMobile = useIsMobile();

  const currentItem = items[currentIndex];

  // Minimum swipe distance required to trigger a swipe
  const minSwipeDistance = 50;

  // Pre-load adjacent images for better performance
  useEffect(() => {
    if (!isOpen || !items.length) return;
    
    const preloadImage = (index: number) => {
      const item = items[index];
      if (item && item.type === 'image') {
        const img = new Image();
        img.src = item.src;
      }
    };
    
    const nextIndex = (currentIndex + 1) % items.length;
    const prevIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
    
    preloadImage(nextIndex);
    preloadImage(prevIndex);
  }, [isOpen, currentIndex, items]);

  // Debug: log do item de vídeo atual
  useEffect(() => {
    if (!isOpen || !currentItem) return;
    if (currentItem.type === 'video') {
      console.log('🎥 GalleryModal - current video item', {
        currentIndex,
        currentItem,
      });
    }
  }, [isOpen, currentIndex, currentItem]);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handlePrevious = () => {
    setCurrentIndex(prev => prev === 0 ? items.length - 1 : prev - 1);
    setIsVideoPlaying(false);
  };

  const handleNext = () => {
    setCurrentIndex(prev => prev === items.length - 1 ? 0 : prev + 1);
    setIsVideoPlaying(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft') {
      handlePrevious();
    } else if (e.key === 'ArrowRight') {
      handleNext();
    }
  };

  const handleShare = () => {
    if (onShare && currentItem) {
      onShare(currentItem);
    }
  };

  const handleVideoToggle = () => {
    setIsVideoPlaying(!isVideoPlaying);
  };

  // Touch handlers for swipe navigation
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentIndex < items.length - 1) {
      handleNext();
    }
    if (isRightSwipe && currentIndex > 0) {
      handlePrevious();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0" 
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative w-full h-full max-w-7xl max-h-screen flex flex-col">
        {/* Header */}
        <div className={cn(
          "flex justify-between items-center bg-black/50 backdrop-blur-sm",
          isMobile ? "p-3" : "p-4 lg:p-6"
        )}>
          <div className="flex items-center gap-2 lg:gap-4">
            <span className={cn(
              "text-white",
              isMobile ? "text-xs" : "text-sm lg:text-base"
            )}>
              {currentIndex + 1} de {items.length}
            </span>
            {currentItem?.type === 'video' && (
              <span className={cn(
                "text-white bg-red-600 px-2 py-1 rounded",
                isMobile ? "text-xs" : "text-sm lg:text-base"
              )}>
                Vídeo
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2 lg:gap-4">
            <button
              onClick={handleShare}
              className={cn(
                "flex items-center gap-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors",
                isMobile ? "p-2" : "px-4 py-2"
              )}
            >
              <Share2 className={cn(isMobile ? "w-4 h-4" : "w-4 h-4")} />
              {!isMobile && <span>Compartilhar</span>}
            </button>
            
            <button
              onClick={onClose}
              className={cn(
                "hover:bg-white/20 rounded-full text-white transition-colors",
                isMobile ? "p-1.5" : "p-2"
              )}
            >
              <X className={cn(isMobile ? "w-5 h-5" : "w-6 h-6")} />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center relative overflow-hidden">
          {/* Navigation Arrows - Visible on ALL devices */}
          {items.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className={cn(
                  "absolute z-20 rounded-full transition-all shadow-lg",
                  "bg-white/90 hover:bg-white text-gray-800",
                  "top-1/2 -translate-y-1/2",
                  isMobile 
                    ? "left-2 p-2" 
                    : "left-4 lg:left-8 p-3"
                )}
                aria-label="Foto anterior"
              >
                <ChevronLeft className={cn(isMobile ? "w-5 h-5" : "w-7 h-7")} />
              </button>
              
              <button
                onClick={handleNext}
                className={cn(
                  "absolute z-20 rounded-full transition-all shadow-lg",
                  "bg-white/90 hover:bg-white text-gray-800",
                  "top-1/2 -translate-y-1/2",
                  isMobile 
                    ? "right-2 p-2" 
                    : "right-4 lg:right-8 p-3"
                )}
                aria-label="Próxima foto"
              >
                <ChevronRight className={cn(isMobile ? "w-5 h-5" : "w-7 h-7")} />
              </button>
            </>
          )}

          {/* Media Display */}
          <div 
            className={cn(
              "w-full h-full flex items-center justify-center overflow-hidden",
              isMobile ? "p-2 px-12" : "p-4 lg:p-6"
            )}
            onTouchStart={isMobile ? onTouchStart : undefined}
            onTouchMove={isMobile ? onTouchMove : undefined}
            onTouchEnd={isMobile ? onTouchEnd : undefined}
          >
            {currentItem?.type === 'video' ? (
              <div className="relative w-full h-full flex items-center justify-center">
                {(() => {
                  // Extrair e validar o ID do YouTube de forma segura (previne XSS)
                  const videoId = extractAndValidateYouTubeId(currentItem.embedHtml || currentItem.src);
                  
                  if (videoId) {
                    // Renderizar iframe seguro com ID validado
                    return (
                      <iframe
                        src={`https://www.youtube.com/embed/${videoId}`}
                        className={cn(
                          "w-full aspect-video",
                          isMobile ? "max-h-[60vh]" : "max-h-[80vh]"
                        )}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="YouTube video player"
                      />
                    );
                  }
                  
                  // Fallback: vídeo direto (MP4, WebM, etc.)
                  return (
                    <div className="relative max-w-full max-h-full">
                      <video
                        src={currentItem.src}
                        className="max-w-full max-h-full object-contain"
                        controls={isVideoPlaying}
                        autoPlay={isVideoPlaying}
                        onClick={handleVideoToggle}
                      />
                      {!isVideoPlaying && (
                        <button
                          onClick={handleVideoToggle}
                          className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors"
                        >
                          <Play className={cn(isMobile ? "w-12 h-12" : "w-16 h-16", "text-white")} />
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            ) : (
              <img
                src={currentItem?.src}
                alt={currentItem?.alt || `Imagem ${currentIndex + 1}`}
                className={cn(
                  "object-contain",
                  isMobile 
                    ? "max-w-[calc(100vw-96px)] max-h-[calc(100vh-140px)]"
                    : "max-w-[calc(100vw-200px)] max-h-[calc(100vh-200px)]"
                )}
                loading="eager"
              />
            )}
          </div>

          {/* Navigation dots - Visible on ALL devices */}
          {items.length > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
              {items.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentIndex(index);
                    setIsVideoPlaying(false);
                  }}
                  className={cn(
                    "rounded-full transition-colors",
                    isMobile ? "w-2 h-2" : "w-3 h-3",
                    index === currentIndex ? "bg-white" : "bg-white/50 hover:bg-white/70"
                  )}
                  aria-label={`Ir para foto ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Thumbnails - Only show on desktop */}
        {items.length > 1 && !isMobile && (
          <div className="p-4 lg:p-6 bg-black/50 backdrop-blur-sm">
            <div className="flex gap-2 overflow-x-auto justify-center">
              {items.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setCurrentIndex(index);
                    setIsVideoPlaying(false);
                  }}
                  className={cn(
                    "relative flex-shrink-0 w-16 h-16 lg:w-20 lg:h-20 rounded-lg overflow-hidden border-2 transition-all",
                    index === currentIndex
                      ? "border-white"
                      : "border-transparent hover:border-white/50"
                  )}
                >
                  <img
                    src={item.thumbnail || item.src}
                    alt={item.alt || `Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  {item.type === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Play className="w-4 h-4 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export { GalleryModal, type GalleryModalProps, type GalleryItem };
export default GalleryModal;
